import { useMemo } from 'react';
import moment from 'moment';

import { bookingStatus } from 'types/booking';

const dateFormat = 'ddd DD MMM YYYY';

const buildRoute = (legs) => {
  if (!legs || !legs.length) {
    return null;
  }

  return {
    dateTime: legs[0]?.timing?.isoDate,
    origin: legs[0]?.origin?.name,
    destination: legs[legs.length - 1]?.destination?.name
  };
};

export const useBookingForAssistanceSearchTable = (bookings) =>
  useMemo(() => {
    if (!bookings) {
      return [];
    }

    const bookingsSortedByReference = [...bookings].sort((a, b) => (a.reference === b.reference ? 0 : a.reference > b.reference ? 1 : -1));

    return bookingsSortedByReference.flatMap(
      ({
        reference,
        trains,
        coachSeat,
        specialAssistance,
        customerFirstName,
        customerLastName,
        departingOn,
        departingFrom,
        departingTo
      }) => ({
        reference,
        status: bookingStatus.CONFIRMED,
        trains,
        firstName: customerFirstName,
        lastName: customerLastName,
        departingOn: moment(departingOn).format('dddd DD MMMM YYYY'),
        departingFrom: departingFrom,
        departingTo: departingTo,
        coachAndSeatForGivenDay: coachSeat,
        specialAssistance
      })
    );
  }, [bookings]);

export const useBookingForStandardSearchTable = (bookings, shouldMapTicketsResponse) =>
  useMemo(() => {
    if (!bookings) {
      return [];
    }

    if (shouldMapTicketsResponse) {
      return bookings.map(({ reference, status, passengers, outboundRoute, inboundRoute }) => ({
        reference,
        status,
        coachAndSeatProducts: passengers.map(({ coach, number }) => ({ carriage: coach, seatNumber: number })),
        coachAndSeatPassengers: passengers.map(({ passenger }) => ({ firstName: passenger.firstName, lastName: passenger.lastName })),
        departingOn: outboundRoute ? moment(outboundRoute.dateTime).format(dateFormat) : '--',
        departingFrom: outboundRoute ? outboundRoute.origin : '--',
        departingTo: outboundRoute ? outboundRoute.destination : '--',
        returningFrom: inboundRoute ? inboundRoute.origin : '--',
        returningTo: inboundRoute ? inboundRoute.destination : '--',
        passengerCount: passengers.length,
        passengers: passengers
      }));
    }

    return bookings.map(
      ({
        reference: bookingReference,
        externalBookingReferences = [],
        expiryTimestamp,
        status,
        passengerCount,
        passengers,
        customer,
        outbound,
        inbound,
        outboundRoute,
        inboundRoute
      }) => {
        const outboundSummary = outboundRoute || buildRoute(outbound);
        const inboundSummary = inboundRoute || buildRoute(inbound);
        const masterReference = externalBookingReferences.reduce(
          (current, { source, externalId }) => (source === 'MASTER' ? externalId : current),
          undefined
        );

        const paymentDue = expiryTimestamp ? moment(expiryTimestamp).format(dateFormat) : undefined;

        return {
          reference: bookingReference,
          masterReference,
          paymentDue,
          status,
          firstName: (customer?.firstName ?? '').replace(
            /\b\w+/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
          ),
          lastName: customer?.lastName?.toUpperCase(),
          passengerCount: passengerCount ? passengerCount : passengers ? passengers.length : 0,
          departingOn: outboundSummary ? moment(outboundSummary.dateTime).format(dateFormat) : '--',
          departingFrom: outboundSummary ? outboundSummary.origin : '--',
          departingTo: outboundSummary ? outboundSummary.destination : '--',
          returningOn: inboundSummary ? moment(inboundSummary.dateTime).format(dateFormat) : '--',
          returningFrom: inboundSummary ? inboundSummary.origin : '--',
          returningTo: inboundSummary ? inboundSummary.destination : '--'
        };
      }
    );
  }, [bookings, shouldMapTicketsResponse]);
